import useStore from '@/store/CreateStore';
import DragIcon from '@/assets/drag-dots.svg?react';
import DeleteIcon from '@/assets/delete.svg?react';
import PencilIcon from '@/assets/pencil.svg?react';
import Button from './Button';
import { useEffect, useRef, useState } from 'react';
import { socket } from '@/services/socket';
import socketEvents, { frontendEvents } from '@/utils/socketEvents';
import { BoardState } from '@/store/interface';
import { DndProvider, useDrag, useDrop, XYCoord } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface DraggableProps {
  board: BoardState & { index: number };
  index: number;
}

const BoardItem = ({
  board,
  index,
  deletable,
  current,
  editName,
  moveBoard,
  hoverBoard,
  disabled,
  onDelete
}: {
  board: BoardState & { index: number };
  index: number;
  deletable: boolean;
  current: boolean;
  editName: (boardName: string, boardId: number, index: number) => void;
  moveBoard: (current: DraggableProps) => void;
  hoverBoard: (oldIndex: number, newIndex: number) => void;
  disabled?: boolean;
  onDelete: (id: number) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(board.boardName || '');

  const setBoard = useStore((state) => state.board.setBoard);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: 'board',
    drop: () => ({ board, index }),
    hover(item: DraggableProps, monitor) {
      if (item.index !== index && containerRef.current) {
        const rect = containerRef.current?.getBoundingClientRect();
        const midY = (rect.bottom - rect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const clientY = (clientOffset as XYCoord).y - rect.top;

        if ((item.index < index && clientY < midY) || (item.index > index && clientY > midY)) {
          return;
        }

        hoverBoard(item.index, index);
        item.index = index;
      }
    }
  });

  const [{ isDragging }, drag] = useDrag(
    {
      type: 'board',
      item: () => {
        return { board, index };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      }),
      end: moveBoard
    },
    [board, index]
  );

  const handleEdit = () => {
    setEditing(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleSubmit = () => {
    if (error) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      return;
    } else {
      setError(null);
      setEditing(false);
      editName(name, board.id, index);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setName(value);
    if (!value.trim()) {
      setError('Board name cannot be empty');
    } else {
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  drag(drop(containerRef));

  return (
    <>
      {editing && (
        <div
          className="fixed top-0 left-0 z-10 h-dvh w-dvw"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        />
      )}
      <div
        ref={containerRef}
        data-current={current}
        data-editing={editing}
        className="relative flex shrink-0 overflow-hidden rounded-lg border text-sm hover:border-blue-500 data-[current=true]:border-blue-500 data-[editing=true]:z-20"
        style={isDragging ? { opacity: 0.5 } : undefined}
      >
        {deletable && !editing && (
          <Button
            mock
            variant="custom"
            Icon={DragIcon}
            rounded={false}
            data-name="board_reordered"
            className="h-full"
          />
        )}
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={name}
            className="flex-1 p-2 text-center outline-hidden aria-invalid:text-red-500 dark:bg-slate-600"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            aria-invalid={!!error}
          />
        ) : (
          <Button
            active={current}
            variant="custom"
            rounded={false}
            className="line-clamp-3 flex-1 text-start whitespace-normal! data-[active=true]:text-blue-500"
            onClick={() => setBoard(index)}
            data-name="board_selected"
          >
            {board.boardName}
          </Button>
        )}
        <Button Icon={PencilIcon} rounded={false} onClick={handleEdit} data-name="board_renamed" />
        {deletable && !editing && (
          <Button
            Icon={DeleteIcon}
            rounded={false}
            variant="danger-hover"
            onClick={() => onDelete(board.id)}
            data-name="board_deleted"
            disabled={disabled}
          />
        )}
      </div>
      {error && <div className="text-xs text-red-500 dark:text-red-600">{error}</div>}
    </>
  );
};

const ManageBoards = () => {
  const [internalBoards, setInternalBoards] = useState<(BoardState & { index: number })[]>([]);

  const roomId = useStore((state) => state.room.roomId);
  const userId = useStore((state) => state.room.userId);
  const boards = useStore((state) => state.board.boards);
  const current = useStore((state) => state.board.current);
  const deleteBoard = useStore((state) => state.board.deleteBoard);
  const setBoardState = useStore((state) => state.board.setBoardState);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = (boardId: number) => {
    setIsDeleting(true);
    deleteBoard(boardId);
    setTimeout(() => {
      setIsDeleting(false);
    }, 500);
  };

  const editName = (boardName: string, boardId: number, index: number) => {
    const newBoards = [...boards];
    newBoards[index].boardName = boardName;
    setBoardState({ boards: newBoards });
    socket.emit(socketEvents.board.DRAW, {
      roomId,
      userId,
      boardData: {
        name: frontendEvents.board.TITLE,
        boardName,
        boardId
      },
      save: true
    });
  };

  const hoverBoard = (oldIndex: number, newIndex: number) => {
    const newBoards = [...internalBoards];
    newBoards.splice(newIndex, 0, newBoards.splice(oldIndex, 1)[0]);
    setInternalBoards(newBoards);
  };

  const moveBoard = (board: DraggableProps) => {
    const oldIndex = board.board.index;
    const newIndex = board.index;
    if (oldIndex !== newIndex) {
      let newCurrent = current;
      if (oldIndex === current) {
        newCurrent = newIndex;
      }
      if (oldIndex < current && newIndex > current) {
        newCurrent -= 1;
      }
      if (oldIndex > current && newIndex < current) {
        newCurrent += 1;
      }
      const newBoards = [...boards];
      newBoards.splice(newIndex, 0, newBoards.splice(oldIndex, 1)[0]);
      setBoardState({ boards: newBoards, current: newCurrent });

      socket.emit(socketEvents.board.DRAW, {
        userId,
        roomId,
        boardData: {
          name: frontendEvents.board.REORDER,
          oldIndex,
          newIndex,
          newCurrent
        },
        save: true
      });
    }
  };

  useEffect(() => {
    setInternalBoards(boards.map((d, i) => ({ ...d, index: i })));
  }, [boards]);

  return (
    <div className="thin-scrollbar flex h-full flex-col gap-2 overflow-y-auto p-2">
      <DndProvider backend={HTML5Backend}>
        <>
          {internalBoards.map((board, index) => (
            <BoardItem
              key={board.id}
              board={board}
              index={index}
              deletable={boards.length > 1}
              current={current === index}
              editName={editName}
              moveBoard={moveBoard}
              hoverBoard={hoverBoard}
              disabled={isDeleting}
              onDelete={handleDelete}
            />
          ))}
        </>
      </DndProvider>
    </div>
  );
};

export default ManageBoards;
